import GlobalStateUtils from '@shared/ipc/GlobalStateUtils.ts'
import { getUserIdentity } from '@shared/utils/amplitude.shared.ts'
import { isAlphaOrDynamicChannel } from '@shared/utils/builds/channels.shared'

/**
 * The rules for who the desktop app is, to PostHog.
 *
 * They lived in five files and were duplicated between the main process and
 * the renderers, which is why fixing one kept breaking another. They are here
 * so there is one place to read them and one place to change them.
 */

/**
 * The deployment the desktop reports as: the flag evaluation context and the
 * `environment` on every event.
 *
 * `NODE_ENV` is `production` in every packaged build, so it can't tell an alpha
 * build from a release. The channel can: alpha and PR builds talk to the
 * staging backend (see `getChatApiUrl`), so flags scoped to staging reach them.
 */
export const analyticsEnvironment = (channel?: string): string => {
  const nodeEnvironment = process.env.NODE_ENV
  if (nodeEnvironment && nodeEnvironment !== 'production') {
    return nodeEnvironment
  }
  return isAlphaOrDynamicChannel(channel) ? 'staging' : 'production'
}

/** The signed-in account, or nothing while signed out. */
export const currentUserId = (): string | undefined => {
  const user = GlobalStateUtils.user.get()
  return !user || user.isAnonymous ? undefined : user.id
}

/**
 * Amplitude's device id, carried as an event property so the two streams stay
 * comparable while both run.
 *
 * Read from Amplitude's own module because it is Amplitude's id; no PostHog
 * identity is derived from it. `getUserIdentity` throws when the ids it reads
 * are missing and this runs at capture time, so letting that escape would drop
 * the event rather than send it without the join key.
 */
const amplitudeIdentity = (): { deviceId?: string; userId?: string } => {
  try {
    return getUserIdentity()
  } catch {
    return {}
  }
}

export const amplitudeJoinKey = (): string | undefined => amplitudeIdentity().deviceId

/**
 * The signed-in account as Amplitude resolves it.
 *
 * Shares the guard above because it is read at capture time, outside any
 * try/catch of ours: a throw there is treated as a failed identity read and
 * the event is dropped, rather than sent without a user id.
 */
export const amplitudeUserId = (): string | undefined => amplitudeIdentity().userId

/** What a change in signed-in state requires of PostHog. */
export type IdentityTransition = 'none' | 'signIn' | 'signOut' | 'switch'

/**
 * Decides what a surface owes PostHog, given who it thinks it already told it.
 *
 * `observedChange` distinguishes a change seen while running from the first
 * read at startup. An absent user means "signed out" only in the first case:
 * at startup it is indistinguishable from auth not having hydrated yet, and
 * `GlobalState` deletes the key on sign-out so both read as undefined. Treating
 * startup as a sign-out emits a false signed-out event and resets PostHog on
 * every signed-in launch, before the real user arrives.
 *
 * Leaving an account always resets first, because posthog-js keeps the
 * identified id in storage: without it, later events still attribute to the
 * account that left and the next person to sign in inherits it.
 */
export function identityTransition({
  userId,
  identifiedUserId,
  observedChange,
}: {
  userId: string | undefined
  identifiedUserId: string | undefined
  observedChange: boolean
}): IdentityTransition {
  if (userId === identifiedUserId) {
    return 'none'
  }
  if (identifiedUserId && userId) {
    return 'switch'
  }
  if (identifiedUserId) {
    return observedChange ? 'signOut' : 'none'
  }
  return userId ? 'signIn' : 'none'
}
